import {
  DisplayPriceRange,
  ProductCardPriceRange,
  TransformedDisplayPriceRange,
  UnitPriceInfo,
  Variant,
  Product,
} from "../../../types/types";
import { PricePerUnit } from "@~/types/prodcatBaseTypes";

const MINIMUM_COST_OF_MADE_TO_MEASURE_SERVICE = 32; // In pounds
const MINIMUM_LENGTH_OF_FABRIC = 0.7; // In metres

export const removeCurrencySymbol = (price: string): string =>
  price.replace("£", "");

export const determinePriceRange = (
  display: DisplayPriceRange,
): TransformedDisplayPriceRange => {
  if (typeof display === "string") return display;

  if (display.max) {
    return display.max === display.min
      ? removeCurrencySymbol(display.max)
      : {
          from: removeCurrencySymbol(display.min),
          to: removeCurrencySymbol(display.max),
        };
  }

  return "";
};

export const transformBlindPrice = (
  price: ProductCardPriceRange,
): ProductCardPriceRange | object => {
  const calcMinimumBlindPrice = (pricePerMetre: string): string => {
    if (Number.isNaN(Number(pricePerMetre))) {
      return pricePerMetre;
    }
    return (
      Math.round(
        Math.ceil(
          100 *
            (MINIMUM_COST_OF_MADE_TO_MEASURE_SERVICE +
              MINIMUM_LENGTH_OF_FABRIC * Number(pricePerMetre)),
        ),
      ) / 100
    ).toString();
  };

  const { now } = price;
  const priceToUse = typeof now === "string" ? now : now.from;
  return {
    ...price,
    now: calcMinimumBlindPrice(priceToUse),
  };
};

export const transformPricePerUnit = (
  pricePerUnit: PricePerUnit | undefined,
): UnitPriceInfo | null => {
  if (!pricePerUnit) return null;

  const { quantity, unitOfMeasure, display } = pricePerUnit;
  return {
    pricePerUnit: {
      currency: "GBP",
      price: Number(removeCurrencySymbol(display).replace(/,/g, "")),
    },
    unit: {
      measure: unitOfMeasure,
      quantity: Number(quantity),
    },
  };
};

export const getComparableVariantPrice = (
  variant: Variant | undefined,
): string | undefined => {
  if (!variant || !("price" in variant)) return undefined;
  return variant.price?.display ?? undefined;
};

export const allVariantsHaveSamePrice = (variants: Variant[]): boolean => {
  if (variants.length <= 1) return true;

  const firstVariantPrice = getComparableVariantPrice(variants[0]);
  if (!firstVariantPrice) return false;

  return variants.every(
    (variant) => getComparableVariantPrice(variant) === firstVariantPrice,
  );
};

export const isPriceAtLeastMinimumThreshold = (
  price: ProductCardPriceRange | null | undefined,
): boolean => {
  if (!price) return false;

  // Some special order items have a non-fixed price that is initially keyed as a
  // value below 5p and then updated to the correct price once the order is placed.
  // We want to hide the price of these products on the PLP.
  const priceToCheck =
    typeof price.now === "string" ? price.now : price.now.from;
  const numericPrice =
    priceToCheck[0] === "£" ? priceToCheck.slice(1) : priceToCheck;
  return numericPrice >= "0.05";
};

export function getCreditMessage(
  price: ProductCardPriceRange,
  product: Product,
): string | undefined {
  if (!product.credit) return undefined;

  const priceNow = price.now;
  const isStringPrice = typeof priceNow === "string";
  const hasNoRange = !isStringPrice && !priceNow.from;

  // Show illustration-based messages for single prices or prices without range
  if ((isStringPrice || hasNoRange) && product.credit.illustration) {
    const { financialProductType, repaymentAmount, creditRate } =
      product.credit.illustration;

    if (financialProductType === "IBC") {
      return `More time to pay from ${repaymentAmount.display} a month ${creditRate.display} APR*`;
    }
    if (financialProductType === "IFC") {
      return `Pay no interest from ${repaymentAmount.display} a month*`;
    }
  } else if (product.credit.available) {
    // Show generic message when no specific illustration message applies
    return "Payment plans available";
  }

  return undefined;
}
